import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Coins, ArrowLeft, Users, Truck, AlertCircle, Plus, Wallet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import CustomerCreditTab from '../../components/admin/credit/CustomerCreditTab';
import SupplierCreditTab from '../../components/admin/credit/SupplierCreditTab';
import TreasuryTab from '../../components/admin/credit/TreasuryTab';

const TABS = [
  { id: 'customers', label: 'Clients', icon: Users, perm: 'credit_customers' },
  { id: 'suppliers', label: 'Fournisseurs', icon: Truck, perm: 'credit_suppliers' },
  { id: 'treasury', label: 'Trésorerie', icon: Wallet, perm: 'treasury' },
];

const AdminCredit = () => {
  const { isStaff, hasPermission } = useAuth();
  const navigate = useNavigate();
  const allowedTabs = TABS.filter((t) => hasPermission(t.perm));
  const [tab, setTab] = useState(() => allowedTabs[0]?.id || 'customers');

  useEffect(() => {
    if (!isStaff || allowedTabs.length === 0) navigate('/admin');
  }, [isStaff, allowedTabs.length, navigate]);

  // If the saved tab isn't permitted any more, fall back to the first allowed one
  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.find((t) => t.id === tab)) {
      setTab(allowedTabs[0].id);
    }
  }, [allowedTabs, tab]);

  if (!isStaff || allowedTabs.length === 0) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-credit-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/admin"
            className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Tableau de bord
          </Link>
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
            <Coins className="w-4 h-4" /> Comptabilité
          </p>
          <h1 className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Dettes & paiements
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Gérez les soldes clients impayés et les factures fournisseurs en attente.
          </p>
        </div>

        {/* Tabs — filtered by permission */}
        <div className="flex gap-1 mb-6 border-b border-white/10">
          {allowedTabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`credit-tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wider border-b-2 transition-colors ${
                  active
                    ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]'
                    : 'border-transparent text-zinc-500 hover:text-white'
                }`}>
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'customers' && <CustomerCreditTab />}
        {tab === 'suppliers' && <SupplierCreditTab />}
        {tab === 'treasury' && <TreasuryTab />}
      </div>
    </div>
  );
};

export default AdminCredit;
